import React, { useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import ReusableTable from '../dashboard/ReusableTable';
import ReusableForm from '../common/ReusableForm';

const ResourceMaster = () => {
  const [resources, setResources] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const toast = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    resource_type: '',
    description: '',
    unit: '',
    capacity: '',
    cost_per_unit: '',
    is_active: true
  });

  const RESOURCE_TYPE_CHOICES = [
    { label: 'Type A', value: 'Type A' },
    { label: 'Type B', value: 'Type B' },
    { label: 'Type C', value: 'Type C' }
  ];

  const formFields = [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter resource name'
    },
    {
      name: 'resource_type',
      label: 'Resource Type',
      type: 'select',
      required: true,
      options: RESOURCE_TYPE_CHOICES
    },
    {
      name: 'description',
      label: 'Description',
      type: 'text',
      placeholder: 'Enter description'
    },
    {
      name: 'unit',
      label: 'Unit',
      type: 'text',
      placeholder: 'Enter unit'
    },
    {
      name: 'capacity',
      label: 'Capacity',
      type: 'text',
      placeholder: 'Enter capacity'
    },
    {
      name: 'cost_per_unit',
      label: 'Cost per Unit',
      type: 'text',
      placeholder: 'Enter cost per unit'
    },
    {
      name: 'is_active',
      label: 'Active',
      type: 'select',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ]
    }
  ];

  const resetForm = () => {
    setFormData({
      name: '',
      resource_type: '',
      description: '',
      unit: '',
      capacity: '',
      cost_per_unit: '',
      is_active: true
    });
    setSelectedResource(null);
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.resource_type) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Name and Resource Type are required',
        life: 3000
      });
      return;
    }

    if (selectedResource) {
      const updatedList = resources.map(res =>
        res.name === selectedResource.name ? formData : res
      );
      setResources(updatedList);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Resource updated successfully',
        life: 3000
      });
    } else {
      setResources([...resources, formData]);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Resource added successfully',
        life: 3000
      });
    }

    setShowDialog(false);
    resetForm();
  };

  const handleEdit = (resource) => {
    setFormData(resource);
    setSelectedResource(resource);
    setShowDialog(true);
  };

  const handleDelete = (resource) => {
    setResources(resources.filter(r => r.name !== resource.name));
    toast.current.show({
      severity: 'success',
      summary: 'Success',
      detail: 'Resource deleted successfully',
      life: 3000
    });
  };

  const columnFields = [
    { field: 'name', header: 'Name', sortable: true },
    { field: 'resource_type', header: 'Resource Type', sortable: true },
    { field: 'description', header: 'Description', sortable: true },
    { field: 'unit', header: 'Unit', sortable: true },
    { field: 'capacity', header: 'Capacity', sortable: true },
    { field: 'cost_per_unit', header: 'Cost/Unit', sortable: true },
    {
      field: 'is_active',
      header: 'Active',
      body: (row) => (
        <span className={`text-sm font-semibold ${row.is_active ? 'text-green-600' : 'text-red-500'}`}>
          {row.is_active ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      field: 'actions',
      header: 'Actions',
      body: (rowData) => (
        <div className="flex gap-2">
          <button
            className="px-2 py-1 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            onClick={() => handleEdit(rowData)}
          >
            Edit
          </button>
          <button
            className="px-2 py-1 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
            onClick={() => handleDelete(rowData)}
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Resource Master</h1>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          onClick={() => {
            resetForm();
            setShowDialog(true);
          }}
        >
          Add Resource
        </button>
      </div>

      <ReusableTable
        data={resources}
        columnFields={columnFields}
        onEdit={handleEdit}
        onDelete={handleDelete}
        showActions={true}
      />

      <Dialog
        visible={showDialog}
        headerClassName="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg"
        header={selectedResource ? 'Edit Resource' : 'Add Resource'}
        onHide={() => {
          setShowDialog(false);
          resetForm();
        }}
        className="rounded-xl shadow-2xl border border-gray-200 overflow-hidden max-w-lg w-full mx-auto"
        breakpoints={{ "960px": "90vw", "640px": "95vw" }}
        contentClassName="p-0"
      >
        <ReusableForm
          formData={formData}
          setFormData={setFormData}
          fields={formFields}
          onSubmit={handleSubmit}
          onHide={() => {
            setShowDialog(false);
            resetForm();
          }}
        />
      </Dialog>
    </div>
  );
};

export default ResourceMaster;
